import { useEffect } from "react";
import {
  Button,
  Checkbox,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormDialog,
  FormDialogCancel,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Label,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import useUsers from "@/hooks/useUsers";
import { dateValue } from "@/utils/legislationMetadataUtils";
import { getInvalidLabel } from "@/config/datasources";
import {
  isLovOrLbk,
  lovOrLbkDocumentTypeDisplayName,
} from "@/utils/legislationDocumentTypeUtils";

export type LegislationMetadataPreview = {
  datasource: string;
  legislationExternalId?: string | null;
  publishedOn?: string | null;
  isCurrent?: boolean | null;
  /** From metadataObject JSON – documentTypeId (e.g. 10 = Lov, 30 = Lovbekendtgørelse); may be string "10"/"30" or number. */
  documentTypeId?: string | number | null;
};

const formSchema = z.object({
  subscriptionName: z.string(),
  includeTimelineUpdates: z.boolean(),
  includeLatest: z.boolean(),
});

type FormValues = z.infer<typeof formSchema>;

type LegislationSubscribeModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entityName: string;
  legislationExternalId?: string;
  legislationDatasource?: string;
  legislationMetadata?: LegislationMetadataPreview;
  isConfirmLoading?: boolean;
  container?: HTMLElement;
  onConfirm: (options: FormValues) => void;
};

export const LegislationSubscribeModal = ({
  open,
  onOpenChange,
  entityName,
  legislationExternalId,
  legislationDatasource,
  legislationMetadata,
  isConfirmLoading = false,
  container,
  onConfirm,
}: LegislationSubscribeModalProps) => {
  const { useLegislationTimelineCheck } = useUsers();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      subscriptionName: entityName,
      includeTimelineUpdates: false,
      includeLatest: false,
    },
  });

  useEffect(() => {
    if (open) {
      form.reset({
        subscriptionName: entityName,
        includeTimelineUpdates: false,
        includeLatest: false,
      });
    }
  }, [open, entityName, form]);

  const timelineCheck = useLegislationTimelineCheck(
    legislationExternalId,
    legislationDatasource,
    open &&
      !!legislationExternalId &&
      !!legislationDatasource &&
      isLovOrLbk(legislationMetadata?.documentTypeId)
  );

  const timelineDisabled =
    timelineCheck.isLoading ||
    timelineCheck.isFetching ||
    (timelineCheck.isSuccess &&
      timelineCheck.data?.hasTimelineSubscription === true);

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      isDirty={form.formState.isDirty}
    >
      <DialogHeader>
        <DialogTitle>Abonner på kilde</DialogTitle>
      </DialogHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onConfirm)}>
          <div className="flex flex-col gap-4 py-4">
            {(legislationMetadata || entityName) && (
              <div className="rounded-md border bg-muted/50 p-3 text-sm">
                <p className="mb-2 font-medium text-muted-foreground">
                  Kildeinformation
                </p>
                <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5">
                  <dt className="text-muted-foreground">Titel:</dt>
                  <dd className="font-medium">{entityName || "–"}</dd>
                  {legislationMetadata && (
                    <>
                      <dt className="text-muted-foreground">Kilde:</dt>
                      <dd>
                        {legislationMetadata.datasource
                          ? legislationMetadata.datasource
                              .charAt(0)
                              .toUpperCase() +
                            legislationMetadata.datasource.slice(1)
                          : "–"}
                      </dd>
                      <dt className="text-muted-foreground">Nummer:</dt>
                      <dd>
                        {legislationMetadata.legislationExternalId || "–"}
                      </dd>
                      <dt className="text-muted-foreground">Dato:</dt>
                      <dd>
                        {legislationMetadata.publishedOn
                          ? dateValue(legislationMetadata.publishedOn)
                          : "–"}
                      </dd>
                      <dt className="text-muted-foreground">Status:</dt>
                      <dd>
                        {legislationMetadata.isCurrent === null ||
                        legislationMetadata.isCurrent === undefined
                          ? "–"
                          : legislationMetadata.isCurrent
                            ? "Gældende"
                            : getInvalidLabel(legislationMetadata.datasource)}
                      </dd>
                      {lovOrLbkDocumentTypeDisplayName(
                        legislationMetadata?.documentTypeId
                      ) && (
                        <>
                          <dt className="text-muted-foreground">Type:</dt>
                          <dd>
                            {lovOrLbkDocumentTypeDisplayName(
                              legislationMetadata?.documentTypeId
                            )}
                          </dd>
                        </>
                      )}
                    </>
                  )}
                </dl>
              </div>
            )}
            <FormField
              control={form.control}
              name="subscriptionName"
              render={({ field }) => (
                <FormItem className="grid gap-2">
                  <FormLabel>Angiv navn på kildeabonnement</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      id="subscribe-name"
                      placeholder={entityName}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {isLovOrLbk(legislationMetadata?.documentTypeId) && (
              <div className="flex items-center space-x-2">
                {(timelineCheck.isLoading || timelineCheck.isFetching) && (
                  <Spinner className="size-4 shrink-0" />
                )}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center space-x-2 min-w-0">
                      <FormField
                        control={form.control}
                        name="includeTimelineUpdates"
                        render={({ field }) => (
                          <Checkbox
                            id="subscribe-timeline"
                            checked={field.value}
                            onCheckedChange={(checked) =>
                              field.onChange(checked === true)
                            }
                            disabled={timelineDisabled}
                          />
                        )}
                      />
                      <Label
                        htmlFor="subscribe-timeline"
                        className={
                          timelineDisabled
                            ? "cursor-not-allowed text-muted-foreground text-sm font-normal"
                            : "cursor-pointer text-sm font-normal"
                        }
                      >
                        Inkluder tidslinjeopdateringer
                      </Label>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent container={container}>
                    {timelineCheck.isSuccess &&
                    timelineCheck.data?.hasTimelineSubscription === true
                      ? (() => {
                          const conf =
                            timelineCheck.data?.conflictingSubscription;
                          if (conf) {
                            const part =
                              conf.legislationName ??
                              `nummer ${conf.legislationExternalId}`;
                            return `Du har allerede et abonnement med tidslinjeopdateringer: ${conf.subscriptionName} (${part}).`;
                          }
                          return "Du har allerede et abonnement, der inkluderer tidslinjeopdateringer for denne lovgivning.";
                        })()
                      : "Inkluder notifikationer ved opdateringer på tidslinjen for denne lovgivning."}
                  </TooltipContent>
                </Tooltip>
              </div>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center space-x-2">
                  <FormField
                    control={form.control}
                    name="includeLatest"
                    render={({ field }) => (
                      <Checkbox
                        id="subscribe-latest"
                        checked={field.value}
                        onCheckedChange={(checked) =>
                          field.onChange(checked === true)
                        }
                      />
                    )}
                  />
                  <Label
                    htmlFor="subscribe-latest"
                    className="cursor-pointer text-sm font-normal"
                  >
                    Abonner på seneste version
                  </Label>
                </div>
              </TooltipTrigger>
              <TooltipContent container={container}>
                Abonnementet opdateres kun ved nye lovbekendtgørelser (LBK)
                eller bekendtgørelser (BEK).
              </TooltipContent>
            </Tooltip>
          </div>
          <DialogFooter>
            <FormDialogCancel />
            <Button type="submit" disabled={isConfirmLoading}>
              Abonner
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </FormDialog>
  );
};

export default LegislationSubscribeModal;
